import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";

import { Body, Header2, Header5 } from "../components/typography";
import DomeHeader from "../components/DomeHeader";
import QuantitySelector from "../components/QuantitySelector";

import richText from "../utils/richText";
import ProductSubscriptionRadio from "../components/ProductSubscriptionRadio";
import { PrimaryButton } from "../components/Button";
import RatingStars from "../components/RatingStars";
import ProductImageSlider from "../components/ProductImageSlider";
import { Colors } from "../components/styles/Colors";

import { store } from "../utils/StoreContext";
import { addOneTimeItem, addSubscriptionItem } from "../utils/StoreActions";
import Breakpoints from "../components/styles/Breakpoints";
import ProductSegmentControl from "../components/ProductSegmentControl";
import OutOfStock from "../components/OutOfStock";

import { gsap, ScrollToPlugin } from "gsap/all";

import Esq from "../assets/images/esq.png";
import Mha from "../assets/images/mha.png";
import Oos from "../assets/images/oos.png";
import { SUBS_ENABLED } from "../utils/settings";
import AddonItem from "../components/AddonItem";
import { getProductSubscriptionData, getPrice } from "../utils/Prive";

gsap.registerPlugin(ScrollToPlugin);

const Wrapper = styled.div`
  background: ${Colors.cream2};

  @media ${Breakpoints.tablet} {
    display: flex;

    flex-direction: row-reverse;

    padding: 30px 0 60px;

    > div {
      flex: 0 0 50%;
    }

    > div:last-child {
      padding-left: 25px;
      margin-right: 30px;
    }
  }

  @media ${Breakpoints.desktop} {
    > div:last-child {
      padding-left: 60px;
    }
  }
`;

const DetailsWrapper = styled.div`
  padding: 40px 32px;

  > ${Header2}:first-of-type {
    margin: 24px 0 40px;
  }

  @media ${Breakpoints.tablet} {
    max-width: 480px;
    margin: 0 auto;
  }
`;

const Description = styled.div`
  p {
    margin: 16px 0;
  }
`;

const VolumeOrSize = styled(Body)`
  font-size: 14px;
  margin: 24px 0 32px;
`;

const ButtonsContainer = styled.div`
  display: flex;

  flex-direction: column-reverse;

  & > button {
    margin-right: 20px;
    margin-top: 20px;
  }

  & > div {
    flex: 0 0 auto;
    height: 64px;
  }

  & > ${Header5} {
    margin-bottom: 20px;
  }

  @media ${Breakpoints.tablet} {
    flex-direction: row;
    align-items: center;

    & > button {
      margin-top: 0;
    }

    & > ${Header5} {
      margin-left: 15px;
      margin-bottom: 0px;
    }
  }
`;

function aggregateRatings(variants) {
  let totalReviews = variants.reduce(
    (acc, v) => acc + v.product?.rating?.totalReviews || 0,
    0
  );
  let summedScore = variants.reduce(
    (acc, v) => acc + v.product?.rating?.score || 0,
    0
  );

  return { count: totalReviews, score: totalReviews / summedScore };
}

export default function ProductDetails({ product }) {
  const { dispatch, state } = useContext(store);
  const { name, description, variants } = product;

  const isOOS = false;

  const multipleVariants = variants.length > 1;
  const isProductSet = product.__typename === "ContentfulProductSet";
  const [selectedVariant, setSelectedVariant] = useState(variants[0]);
  const [selectedAdditionalVariants, setSelectedAdditionalVariants] = useState(
    []
  );
  const [purchaseType, setPurchaseType] = useState(
    isProductSet || !variants[0].subscription || isOOS
      ? "INDIVIDUAL"
      : "SUBSCRIPTION"
  );
  const [cadence, setSubscriptionCadence] = useState();
  const [quantity, setQuantity] = useState(1);
  //PRIVE
  const [options, setOptions] = useState([]);

  const [isRefill, setIsRefill] = useState(0);
  const isShineSheet = name.includes("Shine") && multipleVariants;
  let segmentData = null;

  if (isShineSheet) {
    segmentData = {
      left: "NO-SHINE SHEETS",
      right: "NO-SHINE SHEETS REFILLS",
    };
  }

  useEffect(() => {
    isRefill === 1
      ? setSelectedVariant(variants[1])
      : setSelectedVariant(variants[0]);
  }, [isRefill]);

  function scrollTo() {
    gsap.to(window, {
      duration: 1,
      scrollTo: { y: "#product-reviews", offsetY: 100 },
    });
  }

  function addToCart() {
    let item = {
      name,
      selectedVariant,
    };

    if (product.setItem) {
      const item = {
        name: product.setItem.product.title,
        selectedVariant: {
          individual: {
            shopifyId: product.setItem.shopifyId,
            price: product.setItem.price,
          },
        },
      };

      dispatch(addOneTimeItem(item, quantity, true));
    } else {
      if (isProductSet) {
        product.variants.forEach((variant) => {
          dispatch(
            addOneTimeItem(
              { name: variant.name, selectedVariant: variant },
              quantity,
              true
            )
          );
        });

        selectedAdditionalVariants.forEach((variant) => {
          dispatch(
            addOneTimeItem(
              { name: variant.name, selectedVariant: variant },
              1,
              true
            )
          );
        });
      } else {
        if (purchaseType === "SUBSCRIPTION") {
          dispatch(
            addSubscriptionItem(
              item,
              quantity,
              options?.find((o) => o.sellingPlanId === cadence)
                ?.deliveryCadenceCount,
              true,
              null,
              cadence
            )
          );
        } else {
          dispatch(addOneTimeItem(item, quantity, true));
        }
      }
    }
  }

  function toggleAdditionalVariant(variant) {
    const index = selectedAdditionalVariants.indexOf(variant);
    if (index !== -1) {
      setSelectedAdditionalVariants([]);
    } else {
      setSelectedAdditionalVariants([variant]);
    }
  }

  let ratingsScore;
  let ratingsCount;

  if (isProductSet) {
    const { score, count } = aggregateRatings(product.variants);
    ratingsCount = count;
    ratingsScore = score;
  } else {
    ratingsCount = product?.rating?.totalReviews || 0;
    ratingsScore = product?.rating?.score || 0;
  }

  let seal = undefined;

  if (name === "Invisible Daily SPF 30") {
    seal = Esq;
  } else if (name === "Starter Kit") {
    seal = Mha;
  }
  /* START PRIVE CODE */
  useEffect(() => {
    if (!options || !cadence) return;
    const option = options.find((o) => o.sellingPlanId === cadence);
    product.variants.forEach((v) => {
      v.subscription.price = getPrice(option, v.individual?.priceNumber);
      if (v.id === selectedVariant.id) setSelectedVariant({ ...v });
    });
  }, [options, cadence]);

  useEffect(() => {
    if (!isProductSet) {
      getProductSubscriptionData(product)
        .then((data) => {
          const offerOptions = data?.selling_plan_groups[0].selling_plans;
          setOptions(offerOptions);
          setSubscriptionCadence(
            offerOptions?.find((opt) => opt.deliveryCadenceCount === 3)
              ?.sellingPlanId ||
              offerOptions[0]?.sellingPlanId ||
              null
          );
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);
  /* END PRIVE CODE */

  return (
    <Wrapper>
      <ProductImageSlider seal={seal} slides={product.images} />
      <div>
        <DetailsWrapper>
          <DomeHeader text="Face + Scalp" />
          <Header2>{name}</Header2>
          {isShineSheet && (
            <ProductSegmentControl
              active={isRefill}
              setActive={setIsRefill}
              data={segmentData}
            />
          )}

          <Description>{richText(description.raw)}</Description>

          {/* If just one variant, display the volume/size of that variant */}
          {!multipleVariants && (
            <VolumeOrSize>{variants[0].volumeOrSize}</VolumeOrSize>
          )}

          {!!ratingsCount && (
            <RatingStars
              score={ratingsScore}
              count={ratingsCount}
              scrollTo={scrollTo}
            />
          )}

          {product?.additionalVariants?.map((variant) => (
            <AddonItem
              selected={selectedAdditionalVariants.length > 0}
              variant={variant}
              onSelect={() => toggleAdditionalVariant(variant)}
            />
          ))}

          {!isProductSet && SUBS_ENABLED && !isOOS && (
            <ProductSubscriptionRadio
              selectedVariant={selectedVariant}
              onSelectDuration={setSubscriptionCadence}
              onSelectPurchaseType={setPurchaseType}
              options={options}
            />
          )}

          {isOOS ? (
            <OutOfStock
              product={product}
              onAddToCart={addToCart}
              onQuantityChange={setQuantity}
            />
          ) : (
            <ButtonsContainer>
              <PrimaryButton onClick={addToCart}>
                Add to Cart{" "}
                {isProductSet &&
                  (product.setItem
                    ? `- $${product.setItem.price}`
                    : `- $${[...variants, ...selectedAdditionalVariants].reduce(
                        (a, v) => a + parseFloat(v.individual.price),
                        0
                      )}`)}
              </PrimaryButton>

              <QuantitySelector onChange={setQuantity} />

              {!SUBS_ENABLED && !isProductSet && (
                <Header5>
                  {new Intl.NumberFormat(process.env.GATSBY_LOCALE, {
                    style: "currency",
                    currency:
                      process.env.GATSBY_LOCALE === "en-US" ? "USD" : "GBP",
                  }).format(selectedVariant.individual.price)}
                </Header5>
              )}
            </ButtonsContainer>
          )}
        </DetailsWrapper>
      </div>
    </Wrapper>
  );
}
